import { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown } from "lucide-react";

const FAQ = [
  {
    q: "What actually is Boardroom?",
    a: "It's a personal accountability app that structures weekly and monthly check-ins as recorded 'board meetings' — with yourself, or a partner. You call the session, record it, walk through a fixed agenda (wins, losses, goals, challenges, action items), and Boardroom generates AI meeting minutes and an action plan you can revisit.",
  },
  {
    q: "What's the difference between weekly, monthly, and quarterly sessions?",
    a: "Weekly is your stand-up: quick, tight, 5-section agenda focused on the last 7 days. Monthly is your board review: same core sections plus deep-dives on financials, key metrics, habits audit, strategic pivots, and a big-picture 30-day reflection — and it automatically rolls up all your weekly sessions from the month for context. Quarterly is the full board-of-directors review: an 11-section 90-day retrospective with Category Reflection across the 5 pillars (Health / Business / Relationships / Money / Craft), Vision Alignment, and Next Quarter's Bets — and it rolls up all three of your monthly sessions.",
  },
  {
    q: "Do I have to record audio or video?",
    a: "No. You can just write in each section during the session. Recording is optional — but if you do record, Boardroom transcribes it (via Whisper) and feeds that into the AI minutes so nothing you say is lost.",
  },
  {
    q: "Who reads my session recordings & notes?",
    a: "Only you. Recordings live in your account, minutes are private by default. Sharing to the Founders wall is opt-in, per-session. Admins can see aggregated user counts and orphaned recordings (unclaimed anonymous uploads) but not private meeting content.",
  },
  {
    q: "How do accountability partner sessions work?",
    a: "You invite a partner via a one-time link. Both of you sign in on your own devices, join the same session (WebRTC live video), and share the same agenda + notes doc. When the meeting ends, both parties get the minutes. Great for co-founders, coach/client, or spouses managing a family OS.",
  },
  {
    q: "What plans are available?",
    a: (
      <>
        Free gets you the core recording + minutes flow with sensible limits. Pro
        and Syndicate unlock deeper AI features, more storage, and partner
        billing seats. Full breakdown on the{" "}
        <Link to="/pricing" className="text-[#c89f45] hover:underline">
          pricing page
        </Link>
        .
      </>
    ),
  },
  {
    q: "Can I export my minutes?",
    a: "Yes. Each session's minutes page has a download button (Markdown + PDF-ready HTML). Your data is yours — no lock-in.",
  },
  {
    q: "What happens if I skip a week or month?",
    a: "Nothing dramatic — no shame, no streak reset that punishes you. Boardroom will nudge you via email (opt-out anytime) when the cadence slips, and the next session's opening prompt lets you name why you skipped. Missed monthlies show up in the pattern analysis for future sessions.",
  },
  {
    q: "Is Boardroom a PWA? Can I install it on my phone?",
    a: "Yes. Open foundtheroom.com in Safari (iOS) or Chrome (Android) → Add to Home Screen. You get a branded splash, offline-friendly shell, and native OS push notifications when your session partner joins.",
  },
  {
    q: "Something's broken. How do I report it?",
    a: (
      <>
        Email support directly — details on the{" "}
        <Link to="/contact" className="text-[#c89f45] hover:underline">
          contact page
        </Link>
        . Include a screenshot and the browser you're on.
      </>
    ),
  },
];

/**
 * Static FAQ page. Uses a native <details>/<summary> accordion for zero-JS
 * open-state and native browser Ctrl+F match-in-page semantics — the a11y
 * shadcn Accordion is overkill here.
 */
export default function FAQPage() {
  const [openIdx, setOpenIdx] = useState(0);

  return (
    <div className="max-w-3xl mx-auto px-6 pb-24" data-testid="faq-page">
      <p className="label-eyebrow mt-2 text-[#c89f45]">FAQ · READ BEFORE YOU EMAIL</p>
      <h1 className="font-serif text-4xl sm:text-5xl mt-4 leading-tight">
        Frequently asked.
      </h1>
      <p className="text-[#bbb] text-base sm:text-lg mt-6 leading-relaxed">
        The short answers. If you don't see yours here, hit up the{" "}
        <Link to="/contact" className="text-[#c89f45] hover:underline">
          contact page
        </Link>
        .
      </p>

      <ul className="mt-10 space-y-3" data-testid="faq-list">
        {FAQ.map((item, i) => {
          const isOpen = openIdx === i;
          return (
            <li
              key={i}
              className={`surface rounded-md overflow-hidden transition-colors ${isOpen ? "border-[#c89f45]/30" : ""}`}
              data-testid={`faq-item-${i}`}
            >
              <button
                type="button"
                onClick={() => setOpenIdx(isOpen ? -1 : i)}
                className="w-full flex items-start justify-between gap-4 text-left px-5 py-4 hover:bg-[#101010] transition-colors"
                aria-expanded={isOpen}
                aria-controls={`faq-answer-${i}`}
                data-testid={`faq-question-${i}`}
              >
                <span className="font-serif text-base sm:text-lg text-[#fafafa]">
                  {item.q}
                </span>
                <ChevronDown
                  size={18}
                  className={`shrink-0 mt-1 text-[#888] transition-transform ${isOpen ? "rotate-180 text-[#c89f45]" : ""}`}
                />
              </button>
              {isOpen && (
                <div
                  id={`faq-answer-${i}`}
                  className="px-5 pb-5 text-[#bbb] leading-relaxed"
                  data-testid={`faq-answer-${i}`}
                >
                  {item.a}
                </div>
              )}
            </li>
          );
        })}
      </ul>

      <div className="mt-14 border-t border-[#1a1a1a] pt-8 flex flex-wrap gap-3">
        <Link
          to="/meetings/new"
          className="bg-[#c89f45] hover:bg-[#b48c34] text-[#0a0a0a] font-medium rounded-md px-5 py-2.5 transition-colors"
          data-testid="faq-cta-new"
        >
          Call session to order
        </Link>
        <Link
          to="/sample"
          className="border border-[#2a2a2a] hover:border-[#c89f45] text-[#eaeaea] hover:text-[#c89f45] rounded-md px-5 py-2.5 transition-colors"
          data-testid="faq-cta-sample"
        >
          See a sample session
        </Link>
      </div>
    </div>
  );
}
